import { createAsyncThunk } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import FileDownload from 'js-file-download';
import { DetectionFilter } from '../detectionSlice';
import { CodeusAPI } from 'src/networking/api';

export const exportESFeeds = createAsyncThunk<
	void,
	ExportESFeedsPayload,
	{ rejectValue: string }
>(
	'detections/exportESFeeds',
	async (payload, { rejectWithValue }) => {
		const {
			api,
			id,
			startDate,
			endDate,
			searchText,
			searchField,
			filters,
			sort,
			sortDir,
			status,
		} = payload;

		const formattedStartDate = startDate
			? DateTime.fromISO(startDate)
					.toUTC()
					.toFormat('yyyy-MM-dd HH:mm:ss')
			: null;

		const formattedEndDate = endDate
			? DateTime.fromISO(endDate).toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
			: null;

		// Maps filter fieldName to the backend query param key
		const FILTER_FIELD_MAP: Record<string, string> = {
			cloudType: 'source',
			sharingLevels: 'sharingLevel',
			tags: 'tags',
		};

		// Transform to the format the export API expects: { name, value }
		// Each value gets its own param entry (e.g. tags=Credentials&tags=Credit Card)
		const selectedFilters = filters.flatMap((filter) => {
			const backendKey = FILTER_FIELD_MAP[filter.fieldName] || filter.fieldName;
			return filter.value.map((val) => ({
				name: backendKey,
				value: val,
			}));
		});

		try {
			const result = await api.getAccountDownloadESFeedsV2(
				id,
				formattedStartDate,
				formattedEndDate,
				searchText,
				searchField,
				selectedFilters,
				sort,
				sortDir,
				status,
			);

			FileDownload(
				result,
				`feeds_${DateTime.fromISO(startDate)
					.toLocal()
					.toFormat('MM/dd/yyyy')} - ${DateTime.fromISO(
					endDate ?? startDate,
				)
					.toLocal()
					.toFormat('MM/dd/yyyy')}.csv`,
			);
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: 'An unexpected error occurred';
			return rejectWithValue(message);
		}
	},
);

type ExportESFeedsPayload = {
	api: CodeusAPI;
	id: string;
	startDate: string;
	endDate: string | null;
	searchText: string;
	searchField: string;
	filters: DetectionFilter[];
	sort: string;
	sortDir: string;
	status: string;
};
